import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Lock as LockIcon,
  Person as PersonIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';

import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/quantia';
import { COLORS } from '../theme/theme';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username || !password) {
      setError('Por favor, completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const success = await login(username, password);
      if (success) {
        navigate('/');
      } else {
        setError('Usuario o contraseña incorrectos');
        setPassword('');
        setLoading(false);
      }
    } catch {
      setError('Error de conexión. Inténtalo de nuevo.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: COLORS.black,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
        py: 6,
      }}
    >
      {/* Halos radiales decorativos */}
      <Box
        sx={{
          position: 'absolute',
          top: -120,
          right: -80,
          width: 500,
          height: 500,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(91,79,240,0.35) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -80,
          left: '8%',
          width: 320,
          height: 320,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(194,229,58,0.14) 0%, transparent 65%)',
          pointerEvents: 'none',
        }}
      />

      {/* Tarjeta de login */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 460,
          background: '#FFFFFF',
          borderRadius: '24px',
          boxShadow: '0 24px 48px rgba(0,0,0,0.45)',
          p: { xs: 4, sm: 6 },
        }}
      >
        {/* Badge superior */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            border: '1px solid rgba(91,79,240,0.25)',
            color: COLORS.primaryDeeper,
            background: 'rgba(91,79,240,0.06)',
            borderRadius: 9999,
            px: 1.5,
            py: 0.4,
            fontSize: '0.6875rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            mb: 3,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: COLORS.primary,
            }}
          />
          Acceso seguro
        </Box>

        {/* Logo */}
        <Box sx={{ mb: 1 }}>
          <Logo size="lg" variant="dark" product="Licitator" />
        </Box>

        <Typography
          variant="body2"
          sx={{
            color: COLORS.gray,
            mb: 4,
            mt: 1,
            fontSize: '0.875rem',
          }}
        >
          Inicia sesión para continuar gestionando tus licitaciones con IA.
        </Typography>

        {/* Formulario */}
        <form onSubmit={handleSubmit} noValidate>
          <Typography
            component="label"
            htmlFor="login-username"
            sx={{
              display: 'block',
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: COLORS.grayDark,
              mb: 0.75,
            }}
          >
            Usuario
          </Typography>
          <TextField
            id="login-username"
            fullWidth
            size="small"
            placeholder="Tu usuario"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            inputProps={{ 'aria-describedby': error ? 'login-error' : undefined }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: COLORS.gray }} fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Typography
            component="label"
            htmlFor="login-password"
            sx={{
              display: 'block',
              fontSize: '0.6875rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: COLORS.grayDark,
              mt: 2,
              mb: 0.75,
            }}
          >
            Contraseña
          </Typography>
          <TextField
            id="login-password"
            fullWidth
            size="small"
            placeholder="••••••••"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            inputProps={{ 'aria-describedby': error ? 'login-error' : undefined }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: COLORS.gray }} fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((v) => !v)}
                    edge="end"
                    size="small"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert id="login-error" severity="error" sx={{ mt: 2.5 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
            sx={{ mt: 3, py: 1.5 }}
          >
            {loading ? <CircularProgress size={20} color="inherit" /> : 'Iniciar sesión →'}
          </Button>

          {process.env.NODE_ENV === 'development' && (
            <Alert severity="info" sx={{ mt: 2.5 }}>
              <strong>Desarrollo:</strong> usuario y contraseña se configuran en el
              archivo <code>.env</code>.
            </Alert>
          )}
        </form>

        <Typography
          variant="caption"
          sx={{
            display: 'block',
            textAlign: 'center',
            mt: 3,
            color: COLORS.gray,
            fontSize: '0.75rem',
          }}
        >
          QUANTIA · Ingeniería y Consultoría
        </Typography>
      </Box>
    </Box>
  );
};

export default Login;
